// src/features/auth/sso/providers/GoogleSsoButton.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../shared/context/AuthContext";
import ssoapi from "../../../../services/ssoapi";
import { consumePostLoginRedirect } from "../../utils/postLoginRedirect";
import "../../../../styles/features/auth/login.css";

/** Google renderButton `width` must be an integer in px; allowed range 200–400. */
const clampGoogleButtonWidth = (w) =>
  Math.round(Math.min(400, Math.max(200, w)));

const loadGoogleScriptOnce = () =>
  new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();

    const existing = document.querySelector(
      'script[src="https://accounts.google.com/gsi/client"]',
    );
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("Failed to load Google SSO script")),
      );
      return;
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Failed to load Google SSO script"));
    document.body.appendChild(script);
  });

export default function GoogleSSOButton() {
  const btnRef = useRef(null);
  const navigate = useNavigate();
  const { updateAuthToken } = useAuth();

  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      setError("Missing REACT_APP_GOOGLE_CLIENT_ID");
      return;
    }

    let cancelled = false;
    let resizeObserver = null;
    let debounceTimer = null;
    let lastRenderedWidth = null;

    const measureAndRenderButton = () => {
      if (cancelled || !btnRef.current || !window.google?.accounts?.id) return;
      const measured = btnRef.current.getBoundingClientRect().width;
      if (measured < 1) return;
      const width = clampGoogleButtonWidth(measured);
      if (lastRenderedWidth === width) return;
      lastRenderedWidth = width;
      btnRef.current.innerHTML = "";
      window.google.accounts.id.renderButton(btnRef.current, {
        type: "standard",
        theme: "outline",
        size: "large",
        text: "signin_with",
        shape: "rectangular",
        width,
      });
    };

    const scheduleMeasureAndRender = () => {
      if (cancelled) return;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        measureAndRenderButton();
      }, 100);
    };

    (async () => {
      try {
        await loadGoogleScriptOnce();
        if (cancelled) return;

        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async (response) => {
            // response.credential is the Google ID token (JWT)
            try {
              setBusy(true);
              setError("");

              const appJwt = await ssoapi.exchangeToken(
                "google",
                response.credential,
              );

              updateAuthToken(appJwt);
              const redirected = consumePostLoginRedirect(navigate);
              if (!redirected) {
                navigate("/");
              }
            } catch (e) {
              setError(e.message || "Google SSO failed");
            } finally {
              setBusy(false);
            }
          },
        });

        requestAnimationFrame(() => {
          if (cancelled) return;
          measureAndRenderButton();
          if (cancelled || !btnRef.current) return;
          resizeObserver = new ResizeObserver(scheduleMeasureAndRender);
          resizeObserver.observe(btnRef.current);
        });
      } catch (e) {
        setError(e.message || "Google SSO failed");
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(debounceTimer);
      resizeObserver?.disconnect();
    };
  }, [navigate, updateAuthToken]);

  return (
    <div className="sso-button-wrapper">
      {error && <div className="login-error">{error}</div>}
      <div ref={btnRef} className="sso-google-button-mount" />
      {busy && <div className="sso-loading-message">Signing you in…</div>}
    </div>
  );
}
